import React, { useState, useEffect, useMemo } from 'react';
import { View, LogBox, ScrollView } from 'react-native';
import { interpolateGnBu, interpolateBlues, schemeCategory10 } from 'd3-scale-chromatic';
import LineChartScreen from '../common/charts/lineChart-builder';
import PaperTableDynamic from '../common/controls/TableProvider';
import CustomDT from '../common/controls/customDataTable';
import PieChartComp from '../common/charts/piChart-builder';
import { findImmediateParent, getAllItems, getItemById } from '../common/service/dataOperations';
import { useAuth } from '../../../providers/AuthProvider';
import { useClient } from '../../../providers/ClientProvider';
import { scaleOrdinal } from 'd3-scale';
import { Loader } from '../../Common/Loader';
import { colorType5, logStyles } from './LogStyles';
import { TabScreen, Tabs, TabsProvider } from 'react-native-paper-tabs';
import LogKpiRow from './LogKpiRow';
import LogDateRangeFilter from '../common/controls/LogDateRangeFilter';
import { useNav } from '../../../providers/NavProvider';

interface Props {
  siteId: string;
  logTitle: string;
}

export default function ImportedSoilLogScreen(props: Props) {
  LogBox.ignoreLogs(["EventEmitter.removeListener"]);
  const { sitesResp } = useNav();
  const [LogData, setLogData] = useState<null | any>(null);
  const [LogColumns, setLogColumns] = useState<null | any>(null);
  const { getAccessToken } = useAuth();
  const { clientAPIURL } = useClient();
  useEffect(() => {
    async function fetchData() {
      try {
        const packageSite = sitesResp && findImmediateParent(sitesResp, props.siteId);
        if (packageSite && packageSite.id) {
          const logTitle = props.logTitle;
          const accessToken = await getAccessToken();
          const json = await getAllItems(logTitle, accessToken, clientAPIURL, props.siteId, undefined, packageSite.id);
          setLogData(json.items);
          setLogColumns(json.columns);
        }
      } catch (error) {
        console.error(error);
      }
    }
    sitesResp && fetchData();
  }, [sitesResp]);

  return LogData && LogColumns ? (
    <CreateSoilLogCharts
      LogData={LogData}
      LogColumns={LogColumns}
      siteId={props.siteId}
      logTitle={props.logTitle}
      clientAPIURL={clientAPIURL}
    />
  ) : <Loader />;
}

interface CreateSoilLogChartsProps {
  LogData: any;
  LogColumns: any;
  siteId: string;
  logTitle: string;
  clientAPIURL: string;
}

const CreateSoilLogCharts: React.FC<CreateSoilLogChartsProps> = ({ LogData, LogColumns, siteId, logTitle, clientAPIURL }) => {
  const { getAccessToken } = useAuth();
  const { currentLeafObj } = useNav();
  const defaultTab = currentLeafObj.permissions && JSON.stringify(currentLeafObj.permissions).toLowerCase().includes('write') ? 'list' : 'chart';

  const ColumnsWithIDColumn = LogColumns && [
    ...LogColumns,
    {
      id: 'CODE_GENERATED_ID',
      title: 'Item ID',
      columnName: 'ID',
      list: '7ae860e9-6c67-46d0-b65e-f2869542a3aa',
      datatype: 'calculated',
      createdAt: 'YOUR_CREATED_AT_TIMESTAMP',
      modifiedAt: 'YOUR_MODIFIED_AT_TIMESTAMP',
      required: false,
      subType: 'Float',
      choiceChoices: null,
      choiceType: null,
      choiceMultiselect: null,
      multiline: false,
      order: 0,
      calculatedFormula: null,
      listNavigation: null,
    }
  ];

  const [filteredData, setFilteredData] = useState<any[]>(LogData ?? []);

  // LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string
  // PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string
  const chartData = useMemo(() => {
    const colorLine = scaleOrdinal(schemeCategory10);
    const uniquePeriod = [...new Set(filteredData.map((item: any) => item.Period))] as string[];

    const pieAcc1: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      if (item.SoilType == null) return;
      pieAcc1[item.SoilType] = (pieAcc1[item.SoilType] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const bysoilTypePieData = Object.entries(pieAcc1).map(([name, value], index) => ({
      name, value: Math.round(value), color: colorType5(index.toString()),
    }));

    const pieAcc2: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      if (item.Use == null) return;
      pieAcc2[item.Use] = (pieAcc2[item.Use] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const byUsePieData = Object.entries(pieAcc2).map(([name, value], index) => ({
      name, value: Math.round(value), color: colorType5(index.toString()),
    }));

    const soilTypes = [...new Set(filteredData.map((i: any) => i.SoilType).filter(Boolean))] as string[];
    const groupedBySoil: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      const k = `${item.Period}_${item.SoilType}`;
      groupedBySoil[k] = (groupedBySoil[k] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const lineData = {
      labels: uniquePeriod,
      datasets: soilTypes.map((soilType, index) => ({
        data: uniquePeriod.map(p => groupedBySoil[`${p}_${soilType}`] ?? 0),
        strokeWidth: 2,
        color: (opacity = 1) => colorLine(String(index)) + Math.round(opacity * 255).toString(16),
      })),
    };
    const legendData1 = soilTypes.map(name => ({ name }));

    const useTypes = [...new Set(filteredData.map((i: any) => i.Use).filter(Boolean))] as string[];
    const groupedByUse: Record<string, number> = {};
    filteredData.forEach((item: any) => {
      const k = `${item.Period}_${item.Use}`;
      groupedByUse[k] = (groupedByUse[k] ?? 0) + (Number(item.QuantityTonne) || 0);
    });
    const lineData2 = {
      labels: uniquePeriod,
      datasets: useTypes.map((use, index) => ({
        data: uniquePeriod.map(p => groupedByUse[`${p}_${use}`] ?? 0),
        strokeWidth: 2,
        color: (opacity = 1) => colorLine(String(index + soilTypes.length)) + Math.round(opacity * 255).toString(16),
      })),
    };
    const legendData2 = useTypes.map(name => ({ name }));

    return { uniquePeriod, bysoilTypePieData, byUsePieData, lineData, lineData2, legendData1, legendData2 };
  }, [filteredData]);

  const { mergedData, columnKeys } = useMemo(() => {
    const soilTypeLabels = LogColumns.find((col: any) => col.columnName === 'SoilType')?.choiceChoices ?? '';
    const soilTypesTable: string[] = soilTypeLabels ? soilTypeLabels.split('#;#').map((v: string) => v.split('#;#')[0]) : [];
    const useTypeLabels = LogColumns.find((col: any) => col.columnName === 'Use')?.choiceChoices ?? '';
    const useTypesTable: string[] = useTypeLabels ? useTypeLabels.split('#;#').map((v: string) => v.split('#;#')[0]) : [];

    const groupedDataTable = filteredData.reduce((result: any, item: any) => {
      const { Period, SoilType, QuantityTonne } = item;
      if (!result[Period]) {
        result[Period] = { Period };
        soilTypesTable.forEach((st) => { result[Period][st] = 0; });
      }
      result[Period][SoilType] = (result[Period][SoilType] ?? 0) + Math.round(Number(QuantityTonne) || 0);
      return result;
    }, {});
    const transformedDataTable = Object.values(groupedDataTable).map((values: any) => ({
      ...values,
      'Total (Soil Type)': soilTypesTable.reduce((sum: number, st) => sum + (values[st] ?? 0), 0),
    }));

    const groupedUseDataTable = filteredData.reduce((result: any, item: any) => {
      const { Period, Use, QuantityTonne } = item;
      if (!result[Period]) {
        result[Period] = { Period };
        useTypesTable.forEach((ut) => { result[Period][ut] = 0; });
      }
      result[Period][Use] = (result[Period][Use] ?? 0) + Math.round(Number(QuantityTonne) || 0);
      return result;
    }, {});
    const transformedUseDataTable = Object.values(groupedUseDataTable).map((values: any) => ({
      ...values,
      'Total (Use Type)': useTypesTable.reduce((sum: number, ut) => sum + (values[ut] ?? 0), 0),
    }));

    const combinedData: Record<string, any> = {};
    transformedDataTable.forEach((item: any) => {
      const { Period, ...rest } = item;
      combinedData[Period] = { ...combinedData[Period], ...rest };
    });
    transformedUseDataTable.forEach((item: any) => {
      const { Period, ...rest } = item;
      combinedData[Period] = { ...combinedData[Period], ...rest };
    });
    const mergedData = Object.entries(combinedData).map(([Period, values]) => ({ Period, ...values }));
    const columnKeys = mergedData.length > 0
      ? Object.keys(mergedData[0]).map((key, index) => ({ ColumnName: key, Order: index }))
      : [];

    return { mergedData, columnKeys };
  }, [filteredData, LogColumns]);

  const TotalCount = filteredData.reduce((total: number, item: any) => total + (Number(item.QuantityTonne) || 0), 0);

  const kpiItems = [
    { label: 'Total Soil (Tonnes)', value: Math.round(TotalCount), color: '#00695C' },
    { label: 'Entries', value: filteredData.length, color: '#4527A0' },
  ];

  async function handleDataUpdate(item: any) {
    if (item.ID) {
      try {
        const accessToken = await getAccessToken();
        const result = await getItemById(logTitle, accessToken, clientAPIURL, siteId, item.ID);
        const newItem = result.items[0];
        const updated = filteredData.filter((oItem: any) => oItem.ID !== item.ID);
        setFilteredData([...updated, newItem]);
      } catch (ex) {
        console.error(ex);
      }
    }
  }

  return (
    <View style={logStyles.logRoot}>
      <TabsProvider defaultIndex={defaultTab && defaultTab === 'list' ? 2 : 0}>
        <Tabs style={{ backgroundColor: '#fff', height: "100%" }} showLeadingSpace={true}>
          <TabScreen label="Charts" icon="chart-bar">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View style={{ zIndex: 200 }}>
                <LogKpiRow items={kpiItems}>
                  <LogDateRangeFilter data={LogData ?? []} onFilterChange={setFilteredData} />
                </LogKpiRow>
              </View>
              <View>
                <View style={[logStyles.container]}>
                  {/* PieChartComp contract: data[].name=string (non-null), data[].value=number, data[].color=string */}
                  <View key={1} style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.bysoilTypePieData} colorPallate={interpolateBlues} subTitle={'(Cumulative)'} ChartTitle={"Soil Qty (Tonnes) by Type"} />
                  </View>
                  {/* LineChartScreen contract: labels=string[], datasets[].data=number[], datasets[].color=(opacity)=>string */}
                  <View key={2} style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={chartData.legendData1} data={chartData.lineData} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"Soil Qty (Tonnes) by Type"} />
                  </View>
                  <View key={3} style={[logStyles.card, { backgroundColor: '' }]}>
                    <PieChartComp data={chartData.byUsePieData} colorPallate={interpolateGnBu} subTitle={'(Cumulative)'} ChartTitle={"Soil Qty (Tonnes) by Use"} />
                  </View>
                  <View key={4} style={[logStyles.card, { backgroundColor: '' }]}>
                    <LineChartScreen legend={chartData.legendData2} data={chartData.lineData2} label={chartData.uniquePeriod} subTitle={'(Monthly)'} ChartTitle={"Soil Qty (Tonnes) by Use"} />
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="Tables" icon="table">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {mergedData && columnKeys && (
                      <CustomDT
                        data={mergedData}
                        columns={columnKeys}
                        siteId={siteId}
                        logTitle={logTitle}
                        tableId={'SoilByPeriod'}
                        tableTitle={"Soil summary by period"}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>

          <TabScreen label="List" icon="database">
            <ScrollView contentContainerStyle={logStyles.scrollViewContentContainer}>
              <View>
                <View style={logStyles.container}>
                  <View style={logStyles.cardThree}>
                    {filteredData && ColumnsWithIDColumn && (
                      <PaperTableDynamic
                        data={filteredData}
                        columns={ColumnsWithIDColumn}
                        siteId={siteId}
                        logTitle={logTitle}
                        updateData={handleDataUpdate}
                        isConsolidatedTable={false}
                      />
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </TabScreen>
        </Tabs>
      </TabsProvider>
    </View>
  );
};
